/** @format */

import React, { useState, useMemo } from "react";
import {
  ClockIcon,
  UserPlusIcon,
  ShieldCheckIcon,
  PencilIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";
import usePendingUsers from "../hooks/usePendingUsers";
import LoadingSpinner from "../components/LoadingSpinner";
import ConfirmDialog from "../components/ConfirmDialog";

const PendingUsersPage = () => {
  const { pendingUsers, loading, error, updateUserRole, bulkUpdateUserRoles } =
    usePendingUsers();
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [actionType, setActionType] = useState("");
  const [targetUsers, setTargetUsers] = useState([]);

  // Filter pending users (only users with 'pending' role)
  const filteredPendingUsers = useMemo(() => {
    if (!pendingUsers) return [];

    return pendingUsers.filter((user) => {
      // Only show users with 'pending' role
      if (Array.isArray(user.role)) {
        return user.role.includes("pending");
      }
      return user.role === "pending";
    });
  }, [pendingUsers]);

  // Filter and search users
  const filteredUsers = useMemo(() => {
    let filtered = filteredPendingUsers;

    // Apply status filter
    if (statusFilter !== "all") {
      filtered = filtered.filter((user) => {
        if (statusFilter === "pending-only") {
          if (Array.isArray(user.role)) {
            return user.role.length === 1 && user.role.includes("pending");
          }
          return user.role === "pending";
        }
        if (statusFilter === "student-pending") {
          return (
            Array.isArray(user.role) &&
            user.role.includes("student") &&
            user.role.includes("pending")
          );
        }
        if (statusFilter === "author-pending") {
          return (
            Array.isArray(user.role) &&
            user.role.includes("author") &&
            user.role.includes("pending")
          );
        }
        if (statusFilter === "admin-pending") {
          return (
            Array.isArray(user.role) &&
            user.role.includes("admin") &&
            user.role.includes("pending")
          );
        }
        return true;
      });
    }

    // Apply search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (user) =>
          user.name?.toLowerCase().includes(searchLower) ||
          user.email?.toLowerCase().includes(searchLower) ||
          user.phone?.includes(searchTerm)
      );
    }

    return filtered;
  }, [filteredPendingUsers, searchTerm, statusFilter]);

  // Handle bulk actions
  const handleBulkAction = (action, selectedUserIds) => {
    setActionType(action);
    setTargetUsers(selectedUserIds);
    setShowConfirmDialog(true);
  };

  // Confirm and execute bulk action
  const executeBulkAction = async () => {
    try {
      let newRole = "";

      if (actionType === "promote-to-admin") {
        newRole = "admin";
      } else if (actionType === "promote-to-author") {
        newRole = "author";
      } else if (actionType === "mark-as-student") {
        newRole = "student";
      }

      await bulkUpdateUserRoles(targetUsers, newRole);
      setSelectedUsers([]);
      setShowConfirmDialog(false);
    } catch (error) {
      console.error("Error executing bulk action:", error);
    }
  };

  // Handle individual user action
  const handleUserAction = async (userId, action) => {
    try {
      await updateUserRole(userId, action);
    } catch (error) {
      console.error("Error updating user role:", error);
    }
  };

  // Get user role display
  const getUserRoleDisplay = (user) => {
    if (!user.role || user.role.length === 0) {
      return { text: "No Role", color: "bg-gray-100 text-gray-800" };
    }

    // Handle array format
    if (Array.isArray(user.role)) {
      // Filter out 'pending' and show the actual role(s)
      const actualRoles = user.role.filter((role) => role !== "pending");

      if (actualRoles.length === 0) {
        return { text: "Pending Only", color: "bg-yellow-100 text-yellow-800" };
      }

      if (actualRoles.length === 1) {
        const role = actualRoles[0];
        if (role === "student") {
          return { text: "Student", color: "bg-blue-100 text-blue-800" };
        }
        if (role === "author") {
          return { text: "Author", color: "bg-green-100 text-green-800" };
        }
        if (role === "admin") {
          return { text: "Admin", color: "bg-purple-100 text-purple-800" };
        }
        return {
          text: role.charAt(0).toUpperCase() + role.slice(1),
          color: "bg-gray-100 text-gray-800",
        };
      }

      // Multiple roles (excluding pending)
      return {
        text: `${actualRoles.length} Roles`,
        color: "bg-indigo-100 text-indigo-800",
        tooltip: `Current roles: ${actualRoles.join(", ")}`,
      };
    }

    // Handle string format
    if (typeof user.role === "string") {
      if (user.role === "pending") {
        return { text: "Pending", color: "bg-yellow-100 text-yellow-800" };
      }
      if (user.role === "student") {
        return { text: "Student", color: "bg-blue-100 text-blue-800" };
      }
      if (user.role === "author") {
        return { text: "Author", color: "bg-green-100 text-green-800" };
      }
      if (user.role === "admin") {
        return { text: "Admin", color: "bg-purple-100 text-purple-800" };
      }
      return {
        text: user.role.charAt(0).toUpperCase() + user.role.slice(1),
        color: "bg-gray-100 text-gray-800",
      };
    }

    return { text: "Unknown", color: "bg-gray-100 text-gray-800" };
  };

  // Get user status
  const getUserStatus = (user) => {
    if (!user.role || user.role.length === 0) {
      return {
        text: "No Role Assigned",
        color: "text-gray-500",
        icon: ExclamationTriangleIcon,
      };
    }

    // Handle array format
    if (Array.isArray(user.role)) {
      const hasPending = user.role.includes("pending");
      const actualRoles = user.role.filter((role) => role !== "pending");

      if (actualRoles.length === 0) {
        return {
          text: "Pending Review",
          color: "text-yellow-600",
          icon: ClockIcon,
        };
      }

      if (actualRoles.length === 1) {
        const role = actualRoles[0];
        if (role === "student") {
          return {
            text: "Student (Pending)",
            color: "text-blue-600",
            icon: UserPlusIcon,
          };
        }
        if (role === "author") {
          return {
            text: "Author (Pending)",
            color: "text-green-600",
            icon: PencilIcon,
          };
        }
        if (role === "admin") {
          return {
            text: "Admin (Pending)",
            color: "text-purple-600",
            icon: ShieldCheckIcon,
          };
        }
        return {
          text: `${role.charAt(0).toUpperCase() + role.slice(1)} (Pending)`,
          color: "text-gray-600",
          icon: ExclamationTriangleIcon,
        };
      }

      // Multiple roles with pending
      return {
        text: `${actualRoles.length} Roles + Pending`,
        color: "text-indigo-600",
        icon: ExclamationTriangleIcon,
      };
    }

    // Handle string format
    if (typeof user.role === "string") {
      if (user.role === "pending") {
        return {
          text: "Pending Review",
          color: "text-yellow-600",
          icon: ClockIcon,
        };
      }
      if (user.role === "student") {
        return { text: "Student", color: "text-blue-600", icon: UserPlusIcon };
      }
      if (user.role === "author") {
        return { text: "Author", color: "text-green-600", icon: PencilIcon };
      }
      if (user.role === "admin") {
        return {
          text: "Admin",
          color: "text-purple-600",
          icon: ShieldCheckIcon,
        };
      }
      return {
        text: user.role.charAt(0).toUpperCase() + user.role.slice(1),
        color: "text-gray-600",
        icon: ExclamationTriangleIcon,
      };
    }

    return {
      text: "Unknown Status",
      color: "text-gray-500",
      icon: ExclamationTriangleIcon,
    };
  };

  if (loading) return <LoadingSpinner />;
  if (error) return <div className="text-red-600">Error: {error}</div>;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200/50 p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center space-x-3">
            <div className="bg-gradient-to-br from-yellow-400 to-orange-500 rounded-xl p-3 shadow-lg">
              <ClockIcon className="h-8 w-8 text-white" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
                Pending Users
              </h1>
              <p className="text-sm sm:text-base text-gray-600">
                Manage users with pending role status
              </p>
            </div>
          </div>
          <div className="flex items-center justify-center sm:justify-end">
            <div className="text-center sm:text-right">
              <p className="text-sm text-gray-500">Total Pending Users</p>
              <p className="text-xl sm:text-2xl font-bold text-yellow-600">
                {pendingUsers.length}
              </p>
            </div>
          </div>
        </div>

        {/* Role Breakdown */}
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
            <div>
              <p className="text-xs text-gray-500">Pending Only</p>
              <p className="text-lg font-semibold text-yellow-600">
                {
                  pendingUsers.filter((user) => {
                    if (Array.isArray(user.role)) {
                      return (
                        user.role.length === 1 && user.role.includes("pending")
                      );
                    }
                    return user.role === "pending";
                  }).length
                }
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Student + Pending</p>
              <p className="text-lg font-semibold text-blue-600">
                {
                  pendingUsers.filter((user) => {
                    if (Array.isArray(user.role)) {
                      return (
                        user.role.includes("student") &&
                        user.role.includes("pending")
                      );
                    }
                    return false;
                  }).length
                }
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Author + Pending</p>
              <p className="text-lg font-semibold text-green-600">
                {
                  pendingUsers.filter((user) => {
                    if (Array.isArray(user.role)) {
                      return (
                        user.role.includes("author") &&
                        user.role.includes("pending")
                      );
                    }
                    return false;
                  }).length
                }
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Admin + Pending</p>
              <p className="text-lg font-semibold text-purple-600">
                {
                  pendingUsers.filter((user) => {
                    if (Array.isArray(user.role)) {
                      return (
                        user.role.includes("admin") &&
                        user.role.includes("pending")
                      );
                    }
                    return false;
                  }).length
                }
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200/50 p-4 sm:p-6">
        <div className="flex flex-col gap-4">
          {/* Search and Filter Row */}
          <div className="flex flex-col sm:flex-row gap-3">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search users..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base"
                />
              </div>
            </div>

            {/* Status Filter */}
            <div className="w-full sm:w-48">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base"
              >
                <option value="all">All Pending</option>
                <option value="pending-only">Pending Only</option>
                <option value="student-pending">Student + Pending</option>
                <option value="author-pending">Author + Pending</option>
                <option value="admin-pending">Admin + Pending</option>
              </select>
            </div>
          </div>

          {/* Bulk Actions */}
          {selectedUsers.length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={() =>
                  handleBulkAction("promote-to-admin", selectedUsers)
                }
                className="flex-1 sm:flex-none px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center justify-center space-x-2 text-sm"
              >
                <ShieldCheckIcon className="h-4 w-4" />
                <span className="hidden sm:inline">Make Admin</span>
                <span className="sm:hidden">Admin</span>
              </button>
              <button
                onClick={() =>
                  handleBulkAction("promote-to-author", selectedUsers)
                }
                className="flex-1 sm:flex-none px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2 text-sm"
              >
                <PencilIcon className="h-4 w-4" />
                <span className="hidden sm:inline">Make Author</span>
                <span className="sm:hidden">Author</span>
              </button>
              <button
                onClick={() =>
                  handleBulkAction("mark-as-student", selectedUsers)
                }
                className="flex-1 sm:flex-none px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center space-x-2 text-sm"
              >
                <UserPlusIcon className="h-4 w-4" />
                <span className="hidden sm:inline">Mark Student</span>
                <span className="sm:hidden">Student</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200/50 overflow-hidden">
        <div className="px-4 sm:px-6 py-4 border-b border-gray-200/50">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <h3 className="text-lg font-semibold text-gray-900">
              Users ({filteredUsers.length})
            </h3>
            {selectedUsers.length > 0 && (
              <span className="text-sm text-gray-600">
                {selectedUsers.length} user(s) selected
              </span>
            )}
          </div>
        </div>

        {/* Desktop Table View */}
        <div className="hidden lg:block overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200/50">
            <thead className="bg-gray-50/50">
              <tr>
                <th className="px-6 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={
                      selectedUsers.length === filteredUsers.length &&
                      filteredUsers.length > 0
                    }
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedUsers(filteredUsers.map((u) => u.id));
                      } else {
                        setSelectedUsers([]);
                      }
                    }}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Current Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200/50">
              {filteredUsers.map((user) => {
                const roleDisplay = getUserRoleDisplay(user);
                const status = getUserStatus(user);
                const isSelected = selectedUsers.includes(user.id);

                return (
                  <tr key={user.id} className="hover:bg-gray-50/50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedUsers([...selectedUsers, user.id]);
                          } else {
                            setSelectedUsers(
                              selectedUsers.filter((id) => id !== user.id)
                            );
                          }
                        }}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                            <span className="text-sm font-medium text-white">
                              {user.name?.charAt(0)?.toUpperCase() ||
                                user.email?.charAt(0)?.toUpperCase() ||
                                "U"}
                            </span>
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {user.name || "No Name"}
                          </div>
                          <div className="text-sm text-gray-500">
                            ID: {user.id}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {user.email || "No Email"}
                      </div>
                      <div className="text-sm text-gray-500">
                        {user.phone || "No Phone"}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${roleDisplay.color}`}
                      >
                        {roleDisplay.text}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <status.icon
                          className={`h-4 w-4 mr-2 ${status.color}`}
                        />
                        <span className={`text-sm ${status.color}`}>
                          {status.text}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleUserAction(user.id, "admin")}
                          className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-white bg-purple-600 hover:bg-purple-700 transition-colors"
                          title="Make Admin"
                        >
                          <ShieldCheckIcon className="h-3 w-3 mr-1" />
                          Admin
                        </button>
                        <button
                          onClick={() => handleUserAction(user.id, "author")}
                          className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-white bg-blue-600 hover:bg-blue-700 transition-colors"
                          title="Make Author"
                        >
                          <PencilIcon className="h-3 w-3 mr-1" />
                          Author
                        </button>
                        <button
                          onClick={() => handleUserAction(user.id, "student")}
                          className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-white bg-green-600 hover:bg-green-700 transition-colors"
                          title="Mark as Student"
                        >
                          <UserPlusIcon className="h-3 w-3 mr-1" />
                          Student
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Mobile Card View */}
        <div className="lg:hidden">
          {filteredUsers.map((user) => {
            const roleDisplay = getUserRoleDisplay(user);
            const status = getUserStatus(user);
            const isSelected = selectedUsers.includes(user.id);

            return (
              <div
                key={user.id}
                className="border-b border-gray-200/50 last:border-b-0"
              >
                <div className="p-4">
                  {/* Header with checkbox and avatar */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedUsers([...selectedUsers, user.id]);
                          } else {
                            setSelectedUsers(
                              selectedUsers.filter((id) => id !== user.id)
                            );
                          }
                        }}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mt-1"
                      />
                      <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                        <span className="text-sm font-medium text-white">
                          {user.name?.charAt(0)?.toUpperCase() ||
                            user.email?.charAt(0)?.toUpperCase() ||
                            "U"}
                        </span>
                      </div>
                    </div>
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${roleDisplay.color} relative group cursor-help`}
                      title={roleDisplay.tooltip || roleDisplay.text}
                    >
                      {roleDisplay.text}
                      {roleDisplay.tooltip && (
                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                          {roleDisplay.tooltip}
                          <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                        </div>
                      )}
                    </span>
                  </div>

                  {/* User Info */}
                  <div className="ml-11 space-y-2">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {user.name || "No Name"}
                      </div>
                      <div className="text-xs text-gray-500">ID: {user.id}</div>
                    </div>

                    <div className="space-y-1">
                      <div className="text-sm text-gray-900">
                        {user.email || "No Email"}
                      </div>
                      <div className="text-sm text-gray-500">
                        {user.phone || "No Phone"}
                      </div>
                    </div>

                    {/* Status */}
                    <div className="flex items-center">
                      <status.icon className={`h-4 w-4 mr-2 ${status.color}`} />
                      <span className={`text-sm ${status.color}`}>
                        {status.text}
                      </span>
                    </div>

                    {/* Current Roles Detail */}
                    {Array.isArray(user.role) && user.role.length > 0 && (
                      <div className="text-xs text-gray-600">
                        <span className="font-medium">Current roles:</span>{" "}
                        {user.role.map((role, index) => (
                          <span key={role}>
                            <span
                              className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                                role === "pending"
                                  ? "bg-yellow-100 text-yellow-800"
                                  : role === "student"
                                  ? "bg-blue-100 text-blue-800"
                                  : role === "author"
                                  ? "bg-green-100 text-green-800"
                                  : role === "admin"
                                  ? "bg-purple-100 text-purple-800"
                                  : "bg-gray-100 text-gray-800"
                              }`}
                            >
                              {role}
                            </span>
                            {index < user.role.length - 1 && " "}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex flex-wrap gap-2 pt-2">
                      <button
                        onClick={() => handleUserAction(user.id, "admin")}
                        className="flex-1 sm:flex-none px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center justify-center space-x-2 text-xs"
                      >
                        <ShieldCheckIcon className="h-3 w-3 mr-1" />
                        <span>Admin</span>
                      </button>
                      <button
                        onClick={() => handleUserAction(user.id, "author")}
                        className="flex-1 sm:flex-none px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2 text-xs"
                      >
                        <PencilIcon className="h-3 w-3 mr-1" />
                        <span>Author</span>
                      </button>
                      <button
                        onClick={() => handleUserAction(user.id, "student")}
                        className="flex-1 sm:flex-none px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center space-x-2 text-xs"
                      >
                        <UserPlusIcon className="h-3 w-3 mr-1" />
                        <span>Student</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {filteredUsers.length === 0 && (
          <div className="text-center py-8 sm:py-12 px-4">
            <ClockIcon className="mx-auto h-10 w-10 sm:h-12 sm:w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              No pending users found
            </h3>
            <p className="mt-1 text-xs sm:text-sm text-gray-500 px-4">
              {searchTerm || statusFilter !== "all"
                ? "Try adjusting your search or filter criteria."
                : "All users have been assigned roles."}
            </p>
          </div>
        )}
      </div>

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        onConfirm={executeBulkAction}
        title={`Confirm ${actionType
          .replace(/-/g, " ")
          .replace(/\b\w/g, (l) => l.toUpperCase())}`}
        message={`Are you sure you want to ${actionType.replace(
          /-/g,
          " "
        )} for ${targetUsers.length} user(s)? This action cannot be undone.`}
        confirmText="Confirm"
        cancelText="Cancel"
        confirmButtonStyle="bg-blue-600 hover:bg-blue-700"
      />
    </div>
  );
};

export default PendingUsersPage;
